import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClose } from '@fortawesome/sharp-solid-svg-icons';
import React, { ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';

import { tailwindTypographyClassNameMapping } from '@/components/typography';
import { INPUT_HEIGHT } from '@/constants/input';
import { IFormFieldProps, InputSize } from '@/models/form/form';

import FormController from '../../form/formController';

interface IProps extends IFormFieldProps {
  // eslint-disable-next-line react/require-default-props
  startAdornment?: ReactNode;
}

const BaseTextInput: React.FC<IProps> = ({
  placeholder,
  value,
  onChange,
  id,
  disabled,
  name,
  onFocus,
  className = undefined,
  inputProps,
  onBlur,
  showClearButton = true,
  size = InputSize.MEDIUM,
  startAdornment = undefined,
  type = 'text',
  endAdornment,
}) => {
  const handleClearValue = () => {
    onChange({ target: { value: '' } });
    onBlur?.();
  };

  const renderClearButton = () => {
    if (!value || !showClearButton) return null;

    return (
      <button
        data-testid="clear-button"
        type="button"
        onClick={handleClearValue}
      >
        <FontAwesomeIcon
          className="text-gray-70 hover:text-gray-100"
          icon={faClose}
          size="lg"
        />
      </button>
    );
  };

  return (
    <div
      className={twMerge('flex items-center bg-gray-10', className)}
      style={{ height: INPUT_HEIGHT[size] }}
      onBlur={onBlur}
      onFocus={() => onFocus?.(true)}
    >
      {startAdornment && <div className="ml-4">{startAdornment}</div>}
      <input
        className={twMerge(
          'w-full flex-grow border-none bg-transparent px-4 py-2 font-[Roboto] focus:outline-none focus:ring-0',
          tailwindTypographyClassNameMapping.body1,
          size === InputSize.SMALL && 'px-2'
        )}
        disabled={disabled}
        id={id}
        maxLength={inputProps?.maxLength}
        name={name}
        placeholder={placeholder}
        type={type}
        value={(value as string) || ''}
        onChange={onChange}
      />
      {(endAdornment?.() || renderClearButton()) && (
        <div className="flex items-center space-x-3.5 px-3.5">
          {endAdornment?.()}
          {renderClearButton()}
        </div>
      )}
    </div>
  );
};

// eslint-disable-next-line react/require-default-props
const TextInput: React.FC<IProps> = (props: IProps) => {
  const { control } = props;

  if (control) {
    return <FormController {...props} FormField={BaseTextInput} />;
  }

  return <BaseTextInput {...props} />;
};

export default TextInput;
